import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTrendingDramas, useDramaList } from '@hook/useApi';
import { useAuth } from '@hooks/useAuth';
import { useHeartbeat } from '@hooks/useHeartbeat';
import Header from '@components/Header';
import Footer from '@components/Footer';
import VIPMessage from '@components/VIPMessage';
import BannerAds from '@components/Banner/BannerAds';
import DramaRow from '@components/Drama/DramaRow';
import DramaGrid from '@components/Drama/DramaGrid';
import DramaModal from '@components/Modal/DramaModal';

function Home() {
    const { isLoggedIn } = useAuth();
    
    // ✅ Send heartbeat every 2 minutes to track online users
    useHeartbeat();
    
    const [selectedDrama, setSelectedDrama] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [allDramasPage, setAllDramasPage] = useState(1);
    const [allDramas, setAllDramas] = useState([]);

    // Fetch trending dramas using hook
    const { dramas: trendingData, isLoading: trendingLoading } = useTrendingDramas();

    // Fetch all dramas with pagination using hook
    const { books: currentPageDramas, isLoading: dramasLoading, isMore, mutate } = useDramaList(allDramasPage, 12);

    // ✅ Memoize filtered dramas to prevent re-calculation on every render
    const featuredDramas = useMemo(() => {
        return trendingData?.book?.filter(d =>
            !d.columnTitle || !d.columnTitle.toLowerCase().includes('akan')
        ) || [];
    }, [trendingData]);

    const comingSoonDramas = useMemo(() => {
        return trendingData?.book?.filter(d =>
            d.columnTitle && d.columnTitle.toLowerCase().includes('akan')
        ) || [];
    }, [trendingData]);

    // Append new dramas when page changes
    useEffect(() => {
        if (currentPageDramas && currentPageDramas.length > 0) {
            if (allDramasPage === 1) {
                // First page, replace all
                setAllDramas(currentPageDramas);
            } else {
                // Subsequent pages, append
                setAllDramas(prev => {
                    // Prevent duplicates by checking bookId
                    const existingIds = new Set(prev.map(d => d.id));
                    const newDramas = currentPageDramas.filter(d => !existingIds.has(d.id));
                    return [...prev, ...newDramas];
                });
            }
        }
    }, [currentPageDramas, allDramasPage]);

    // ✅ Memoize callbacks to prevent re-creation
    const handleOpenModal = useCallback((drama) => {
        setSelectedDrama(drama);
        setIsModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        setTimeout(() => setSelectedDrama(null), 300);
    }, []);

    const handleLoadMore = useCallback(() => {
        if (!dramasLoading && isMore) {
            setAllDramasPage(prev => prev + 1);
        }
    }, [dramasLoading, isMore]);

    return (
        <div className="homepage">
            <Header />

            <VIPMessage />

            <BannerAds />

            <main className="main-content">
                {/* Loading State */}
                {trendingLoading && !trendingData ? (
                    <div className="loading-section">
                        <div className="loading-spinner">
                            <div className="spinner"></div>
                            <p>Memuat koleksi drama...</p>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Featured Dramas Row */}
                        <DramaRow
                            dramas={featuredDramas}
                            title="Drama Pilihan"
                            onOpenModal={handleOpenModal}
                        />

                        {/* Coming Soon Row */}
                        {comingSoonDramas.length > 0 && (
                            <DramaRow
                                dramas={comingSoonDramas}
                                title="Akan Datang"
                                onOpenModal={handleOpenModal}
                            />
                        )}

                        {/* All Dramas Grid */}
                        <section className="drama-section" style={{ display: 'block' }}>
                            <h3 className="row-title">Semua Drama</h3>

                            <DramaGrid
                                dramas={allDramas}
                                onOpenModal={handleOpenModal}
                                isLoading={allDramasPage === 1 && dramasLoading}
                            />

                            {/* Load More Button */}
                            <div className="load-more-container" style={{ textAlign: 'center', marginTop: '2rem' }}>
                                {isMore && !dramasLoading && (
                                    <button className="load-more-btn" onClick={handleLoadMore}>
                                        <i className="fas fa-plus"></i> Muat 12 Drama Lagi
                                    </button>
                                )}
                                {dramasLoading && allDramasPage > 1 && (
                                    <div className="loading-more">
                                        <i className="fas fa-spinner fa-spin"></i> Memuat...
                                    </div>
                                )}
                                {!isMore && allDramas.length > 0 && (
                                    <p style={{ color: '#999', marginTop: '1rem' }}>
                                        Semua drama telah ditampilkan
                                    </p>
                                )}
                            </div>
                        </section>
                    </>
                )}
            </main>

            {/* Drama Detail Modal */}
            <DramaModal
                drama={selectedDrama}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
            />

            <Footer />
        </div>
    );
}

export default Home;
